import React from 'react';

import {FormControl, Grid, MenuItem, Stack, TextField} from '@mui/material';
import {useForm} from 'react-hook-form';
import {defineMessages, useIntl} from 'react-intl';
import {useNavigate} from 'react-router-dom';
import {useMutation, useQuery, useQueryClient} from 'react-query';

import API, {Types, MemberUpdateRequest, useCsrfToken} from '@wheeebo/polaris-api';
import {useSnackbar} from '@wheeebo/polaris-snackbar';
import {CircleLoader, LoadError, ValidatableField, useErrorHandler} from '@wheeebo/polaris-ui';

const msgs = defineMessages({
  errorNoMember: {
    id: 'form.MemberEditForm.error.noMember',
    defaultMessage: 'member data not found',
  },
  buttonCancel: {
    id: 'form.MemberEditForm.button.cancel',
    defaultMessage: 'Cancel',
  },
  buttonSubmit: {
    id: 'form.MemberEditForm.button.submit',
    defaultMessage: 'Submit',
  },
  inputNameLabel: {
    id: 'form.MemberEditForm.input.nameLabel',
    defaultMessage: 'Name',
  },
  inputNamePlaceholder: {
    id: 'form.MemberEditForm.input.namePlaceholder',
    defaultMessage: 'Member name',
  },
  inputNameHelperText1: {
    id: 'form.MemberEditForm.input.nameHelperText1',
    defaultMessage: 'Enter the member name, which can give you hints for identifying your member.',
  },
  successMemberUpdated: {
    id: 'form.MemberEditForm.success.accountUpdated',
    defaultMessage: 'Member updated',
  },
});

interface Props {
  memberId?: string;
  locked?: boolean;
  showFormSubmitButton?: boolean;
}

function MemberEditForm({memberId, locked = false, showFormSubmitButton = false}: Props): JSX.Element {
  const intl = useIntl();
  const {csrfToken} = useCsrfToken();
  const navigate = useNavigate();
  const {register, formState} = useForm({mode: 'onChange'});
  const {errors} = formState;
  const {showAndEmitJsErrorEvent, showError} = useErrorHandler();
  const {showSuccess} = useSnackbar();
  const queryClient = useQueryClient();
  const [memberRole, setMemberRole] = React.useState<Types.MemberRole>(Types.MemberRoleMember);
  const {data, isError, isLoading} = useQuery(
    memberId ? API.Members.getKey(memberId) : '',
    () => (memberId ? API.Members.get(memberId) : undefined),
    {enabled: !!memberId},
  );
  const {mutate: updateMember} = useMutation<unknown, unknown, MemberUpdateRequest>(
    (props: MemberUpdateRequest) => {
      if (!data) {
        showError(new Error(intl.formatMessage(msgs.errorNoMember)));
        return Promise.reject({});
      }
      return API.Members.update(csrfToken, data.id, props);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(API.Members.CacheKey);
        showSuccess(intl.formatMessage(msgs.successMemberUpdated));
        navigate('.', {replace: true});
      },
      onError: showAndEmitJsErrorEvent,
    },
  );
  const saveMemberRole = (newValue: string) => {
    if (!errors['role']) {
      updateMember({role: newValue});
      return true;
    }
    return false;
  };

  if (isLoading) {
    return <CircleLoader />;
  } else if (isError) {
    return <LoadError />;
  }
  return (
    <Grid container>
      <Grid item xs={12}>
        <Stack gap={1} sx={{flex: 1}}>
          <TextField disabled fullWidth variant="standard" label="Member ID" value={memberId} />
          <TextField disabled fullWidth variant="standard" label="Name" value={data?.name} />
          <FormControl component="fieldset" fullWidth>
            <ValidatableField
              select
              id="member_role"
              label="Member Role"
              placeholder="MEMBER or ROUTE_ADMIN or SYSTEM_ADMIN"
              inputProps={{
                sx: {display: 'flex', alignItems: 'center'},
              }}
              helperTexts={['Choose the role of this member']}
              value={memberRole || Types.MemberRoleMember}
              defaultValue={data?.role || Types.MemberRoleMember}
              registerOptions={{
                value: data?.role || Types.MemberRoleMember,
                required: true,
                onChange: (e) => setMemberRole(e.target.value),
              }}
              onSave={saveMemberRole}
              editable={!showFormSubmitButton}
              {...{register, errors, locked}}>
              <MenuItem value={Types.MemberRoleMember}>{Types.MemberRoleMember}</MenuItem>
              <MenuItem value={Types.MemberRoleTrafficController}>{Types.MemberRoleTrafficController}</MenuItem>
              <MenuItem value={Types.MemberRoleSystemAdmin}>{Types.MemberRoleSystemAdmin}</MenuItem>
            </ValidatableField>
          </FormControl>
        </Stack>
      </Grid>
    </Grid>
  );
}

export default React.memo(MemberEditForm);
